import { colors } from "@areahub/theme";
import { StackProps } from "@chakra-ui/react";
import {
  DefaultControls,
  DynamicLegend,
  MapWrapper,
  UserLocationPin,
} from "@ui/maps/common/src";
import SubscriberMapPopup from "@ui/maps/interactive-map/src/subscriber-map-popup";
import { type Ref } from "react";
import Map, {
  FullscreenControl,
  Layer,
  Source,
  type MapRef,
  type SourceProps,
} from "react-map-gl";
import { getHazardMapLayerStyle } from "./helpers";
import {
  useProReportHazardMap,
  type UseProReportHazardMapOptions,
} from "./hooks/use-pro-report-hazard-maps";
export interface ProReportHazardMapProps extends UseProReportHazardMapOptions {
  wrapperProps?: StackProps;
  mapboxAccessToken?: string;
}

export const ProReportHazardMap = ({
  wrapperProps,
  ...props
}: ProReportHazardMapProps) => {
  const {
    ref,
    buffer,
    initialViewState,
    resetViewState,
    legendRows,
    hovered,
    clicked,
    onLoad,
    onMouseMove,
    onClick,
    color,
  } = useProReportHazardMap(props);
  return (
    <MapWrapper
      sx={{
        ".mapboxgl-popup-close-button": {
          display: "none",
        },
      }}
      position="relative"
      {...wrapperProps}
    >
      <Map
        ref={ref as Ref<MapRef>}
        onClick={onClick}
        onMouseMove={onMouseMove}
        onLoad={onLoad}
        initialViewState={initialViewState}
        mapboxAccessToken={
          props?.mapboxAccessToken ??
          process.env.CLIENT_MAPBOX_ACCESS_TOKEN ??
          process.env.NEXT_PUBLIC_CLIENT_MAPBOX_ACCESS_TOKEN
        }
        attributionControl={false}
        // disable pitch
        maxPitch={0}
        // disable rotation
        dragRotate={false}
        // disable scroll zooming
        scrollZoom={false}
        mapStyle={
          "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
        }
        style={{ height: "100%", width: "100%" }}
        interactiveLayerIds={[props.category]}
      >
        <Source
          {...{
            id: "radius",
            data: buffer,
            type: "geojson",
          }}
        >
          <Layer
            type="fill"
            paint={{
              "fill-opacity": 0.25,
              "fill-color": color ?? colors.blue[300],
            }}
            id="radius"
          />
        </Source>
        <Source
          {...({
            id: props.category,
            data: {
              type: "FeatureCollection",
              features: props?.features?.map((x) => ({
                ...x,
                type: "Feature",
              })),
            },
            type: "geojson",
          } as SourceProps)}
        >
          <Layer
            {...getHazardMapLayerStyle(props?.features?.[0]?.geometry)}
            beforeId="radius"
            id={props.category}
          />
        </Source>
        <DefaultControls resetViewport={resetViewState}>
          <FullscreenControl position="bottom-right" />
        </DefaultControls>
        <DynamicLegend
          legendRows={legendRows}
          position="absolute"
          bottom="1"
          left="1"
          inverted
        />

        <UserLocationPin userLocation={props.userLocation} />
        {hovered && <SubscriberMapPopup {...hovered} />}
        {clicked && <SubscriberMapPopup {...clicked} />}
      </Map>
    </MapWrapper>
  );
};
